import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import {
  AuthorizationEvent,
  AuthorizationHistoryResponse,
  FilterParams, PaginationInfo,
  ProcessedEvent
} from '../models/habilitation-history.model';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationHistoryService {

  private baseUrl = `${environment.apiUrl}`;
  private readonly apiUrl = `${this.baseUrl}/habilitations/all`; // Correction du "+" en trop

  // BehaviorSubjects pour gérer l'état
  private eventsSubject = new BehaviorSubject<ProcessedEvent[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private paginationSubject = new BehaviorSubject<PaginationInfo | null>(null);
  private errorSubject = new BehaviorSubject<string | null>(null);

  // Observables publics
  public events$ = this.eventsSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public pagination$ = this.paginationSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Récupère la liste des événements d'historique
   */
  getAllHistoriques(filters: FilterParams = {}): Observable<AuthorizationHistoryResponse> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    // Construction des paramètres HTTP
    let params = new HttpParams();

    if (filters.operationType) {
      params = params.set('operationType', filters.operationType);
    }

    if (filters.resourceType) {
      params = params.set('resourceType', filters.resourceType);
    }

    if (filters.page !== undefined) {
      params = params.set('page', filters.page.toString());
    }

    if (filters.size !== undefined) {
      params = params.set('size', filters.size.toString());
    }

    if (filters.sort) {
      params = params.set('sort', filters.sort);
    }

    if (filters.startDate) {
      params = params.set('startDate', filters.startDate);
    }

    if (filters.endDate) {
      params = params.set('endDate', filters.endDate);
    }

    if (filters.userId) {
      params = params.set('userId', filters.userId);
    }

    if (filters.clientId) {
      params = params.set('clientId', filters.clientId);
    }

    if (filters.clientUuid) {
      params = params.set('clientUuid', filters.clientUuid);
    }

    return this.http.get<AuthorizationHistoryResponse>(this.apiUrl, { params })
      .pipe(
        tap(response => {
          const processedEvents = this.processApiData(response.content);
          this.eventsSubject.next(processedEvents);

          const paginationInfo: PaginationInfo = {
            currentPage: response.number,
            totalPages: response.totalPages,
            totalElements: response.totalElements,
            size: response.size,
            numberOfElements: response.numberOfElements,
            first: response.first,
            last: response.last
          };
          this.paginationSubject.next(paginationInfo);

          this.loadingSubject.next(false);
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Traite les données brutes de l'API
   */
  processApiData(events: AuthorizationEvent[]): ProcessedEvent[] {
    return events.map((event, index) => {
      const dateTime = this.formatDateTime(event.time);
      const roles = this.extractRoles(event.representation);

      return {
        ...event,
        ...dateTime,
        operationClass: this.getOperationClass(event.operationType),
        roles: roles,
        userId: event.authDetails?.user.id || 'N/A',
        username: event.authDetails?.user.username || 'N/A',
        userFullName: event.authDetails?.user
          ? `${event.authDetails.user.firstName} ${event.authDetails.user.lastName}`.trim()
          : 'N/A',
        userEmail: event.authDetails?.user.email || 'N/A',
        clientId: event.authDetails?.clientId || 'N/A',
        ipAddress: event.authDetails?.ipAddress || 'N/A'
      };
    });
  }

  /**
   * Extrait les rôles de la représentation
   */
  private extractRoles(representation: any): string[] {
    if (!representation) return [];

    try {
      // Si c'est déjà un array
      if (Array.isArray(representation)) {
        return representation
          .map(role => role.name)
          .filter(name => name && name.trim() !== '');
      }

      // Si c'est une string JSON
      if (typeof representation === 'string') {
        const parsed = JSON.parse(representation);
        if (Array.isArray(parsed)) {
          return parsed
            .map(role => role.name || 'Rôle inconnu')
            .filter(name => name && name.trim() !== '');
        }
      }

      return [];
    } catch (error) {
      console.warn('Erreur lors de l\'extraction des rôles:', error);
      return [];
    }
  }

  /**
   * Formate la date et l'heure
   */
  private formatDateTime(timestamp: number): { dateString: string; timeString: string } {
    const date = new Date(timestamp);
    return {
      dateString: date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      timeString: date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    };
  }

  /**
   * Obtient la classe CSS pour le type d'opération
   */
  private getOperationClass(operationType: string): string {
    return operationType.toLowerCase();
  }

  /**
   * Gestion des erreurs HTTP
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    this.loadingSubject.next(false);

    let errorMessage: string;

    if (error.error instanceof ErrorEvent) {
      // Erreur côté client
      errorMessage = `Erreur: ${error.error.message}`;
    } else {
      // Erreur côté serveur
      switch (error.status) {
        case 400:
          errorMessage = 'Requête invalide. Vérifiez les paramètres.';
          break;
        case 401:
          errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
          break;
        case 403:
          errorMessage = 'Accès interdit. Vous n\'avez pas les permissions nécessaires.';
          break;
        case 404:
          errorMessage = 'Ressource non trouvée.';
          break;
        case 500:
          errorMessage = 'Erreur serveur interne.';
          break;
        case 0:
          errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
          break;
        default:
          errorMessage = `Erreur ${error.status}: ${error.message}`;
      }
    }

    this.errorSubject.next(errorMessage);
    console.error('Erreur API:', error);

    return throwError(() => new Error(errorMessage));
  }

  /**
   * Réinitialise l'état du service
   */
  resetState(): void {
    this.eventsSubject.next([]);
    this.loadingSubject.next(false);
    this.paginationSubject.next(null);
    this.errorSubject.next(null);
  }
}